/**
 * @fileoverview Doctor Schedule React Query Hooks
 * @description Custom hooks for fetching doctor assignments and weekly schedules
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - React Query with proper caching
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { 
  getUpcomingAssignments, 
  getWeeklySchedule,
  getCurrentWeekStart,
  formatDateISO,
} from '../../api/endpoints/doctor';
import type {
  DoctorAssignment,
  WeeklyScheduleData,
  UpcomingAssignmentsFilters,
  ApiResponse,
} from '../../types/doctor';

/**
 * React Query keys for doctor schedule
 */
export const DOCTOR_SCHEDULE_KEYS = {
  all: ['doctor-schedule'] as const,
  upcomingAssignments: (filters?: UpcomingAssignmentsFilters) => 
    [...DOCTOR_SCHEDULE_KEYS.all, 'upcoming', filters] as const,
  weeklySchedule: (weekStart: string) => 
    [...DOCTOR_SCHEDULE_KEYS.all, 'weekly', weekStart] as const,
} as const;

/**
 * Hook for fetching upcoming assignments with filters
 * @param filters - Optional filters (startDate, endDate, buildingId, sessionLabel)
 * @returns React Query result with assignments list
 * 
 * @example
 * ```tsx
 * function UpcomingAssignmentsList() {
 *   const [filters, setFilters] = useState<UpcomingAssignmentsFilters>({});
 *   const { data, isLoading, error } = useUpcomingAssignments(filters);
 * 
 *   if (isLoading) return <Skeleton />;
 *   if (error) return <ErrorDisplay />;
 * 
 *   const assignments = data?.data || [];
 *   return <Table data={assignments} />;
 * }
 * ```
 */
export function useUpcomingAssignments(
  filters?: UpcomingAssignmentsFilters
): UseQueryResult<ApiResponse<DoctorAssignment[]>, Error> {
  return useQuery({
    queryKey: DOCTOR_SCHEDULE_KEYS.upcomingAssignments(filters),
    queryFn: () => getUpcomingAssignments(filters),
    
    // Cache configuration
    staleTime: 3 * 60 * 1000, // 3 minutes - assignments may change more frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
    
    // Refetch configuration
    refetchOnWindowFocus: true,
    refetchInterval: 3 * 60 * 1000, // Background refetch every 3 minutes
    
    // Error handling
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    networkMode: 'online',
  });
}

/**
 * Hook for fetching weekly schedule (assignments grouped by day)
 * @param weekStart - Week start date (Date object or ISO string)
 * @returns React Query result with weekly schedule data
 * 
 * @example
 * ```tsx
 * function WeeklyCalendar() {
 *   const [currentWeekStart, setCurrentWeekStart] = useState(getCurrentWeekStart());
 *   const { data, isLoading } = useWeeklySchedule(currentWeekStart);
 * 
 *   if (isLoading) return <CalendarSkeleton />;
 * 
 *   const schedule = data?.data || {};
 *   return <WeeklyCalendarView schedule={schedule} />;
 * }
 * ```
 */
export function useWeeklySchedule(
  weekStart: Date | string
): UseQueryResult<ApiResponse<WeeklyScheduleData>, Error> {
  // Convert to ISO string for consistent cache key
  const weekStartISO = typeof weekStart === 'string' 
    ? weekStart 
    : formatDateISO(weekStart);

  return useQuery({
    queryKey: DOCTOR_SCHEDULE_KEYS.weeklySchedule(weekStartISO),
    queryFn: () => getWeeklySchedule(weekStart),
    
    // Cache configuration
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep multiple weeks cached
    
    // Refetch configuration
    refetchOnWindowFocus: true,
    refetchInterval: 3 * 60 * 1000, // Background refetch every 3 minutes
    
    // Error handling
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    networkMode: 'online',
  });
}

/**
 * Hook for fetching current week's schedule
 * @returns React Query result with current week's schedule
 * 
 * @description
 * Convenience hook that automatically uses the current week's start date
 */
export function useCurrentWeekSchedule(): UseQueryResult<ApiResponse<WeeklyScheduleData>, Error> {
  const weekStart = getCurrentWeekStart();
  return useWeeklySchedule(weekStart);
}

/**
 * Extract assignments safely from upcoming assignments query result
 * @param result - React Query result from useUpcomingAssignments
 * @returns Assignments array or empty array if not available
 */
export function extractAssignments(
  result: UseQueryResult<ApiResponse<DoctorAssignment[]>, Error>
): DoctorAssignment[] {
  if (result.data?.success && result.data.data) {
    return result.data.data;
  }
  return [];
}

/**
 * Extract weekly schedule safely from query result
 * @param result - React Query result from useWeeklySchedule
 * @returns Weekly schedule data or empty object if not available
 */
export function extractWeeklySchedule(
  result: UseQueryResult<ApiResponse<WeeklyScheduleData>, Error>
): WeeklyScheduleData {
  if (result.data?.success && result.data.data) {
    return result.data.data;
  }
  return {};
}

/**
 * Get error message from schedule query result
 * @param result - React Query result
 * @returns User-friendly error message or null
 */
export function getScheduleError(
  result: UseQueryResult<ApiResponse<any>, Error>
): string | null {
  if (result.error) {
    return result.error.message || 'Failed to load schedule data';
  }
  
  if (result.data && !result.data.success && result.data.error) {
    return result.data.error.message || 'Failed to load schedule data';
  }
  
  return null;
}

/**
 * Count total assignments from filters result
 * @param result - React Query result from useUpcomingAssignments
 * @returns Total number of assignments
 */
export function countAssignments(
  result: UseQueryResult<ApiResponse<DoctorAssignment[]>, Error>
): number {
  return extractAssignments(result).length;
}

/**
 * Count total assignments in a week
 * @param result - React Query result from useWeeklySchedule
 * @returns Total number of assignments across all days
 */
export function countWeeklyAssignments(
  result: UseQueryResult<ApiResponse<WeeklyScheduleData>, Error>
): number {
  const schedule = extractWeeklySchedule(result);
  return Object.values(schedule).reduce((total, dayAssignments) => total + dayAssignments.length, 0);
}

/**
 * Get assignments for a specific date
 * @param schedule - Weekly schedule data
 * @param date - Date object or ISO string (YYYY-MM-DD)
 * @returns Array of assignments for that date
 */
export function getAssignmentsForDate(
  schedule: WeeklyScheduleData,
  date: Date | string
): DoctorAssignment[] {
  const dateKey = typeof date === 'string' ? date : formatDateISO(date);
  return schedule[dateKey] || [];
}

/**
 * Check if a specific date has assignments
 * @param schedule - Weekly schedule data
 * @param date - Date object or ISO string
 * @returns True if date has at least one assignment
 */
export function hasAssignmentsOnDate(
  schedule: WeeklyScheduleData,
  date: Date | string
): boolean {
  return getAssignmentsForDate(schedule, date).length > 0;
}

